import { useState } from "react";
import Toggle from "@/components/base/Toggle";
import {
  carServices,
  vehicleTypes,
  carPriceGrid,
  carOptions,
  homeElements,
  garments,
  washTypes,
  delivery,
  type CarService,
  type CarOption,
  type Garment,
  type HomeElement,
  type DeliveryMode,
} from "@/mocks/services";

type ServiceTab = "car" | "home" | "laundry";

const tabs: { key: ServiceTab; label: string }[] = [
  { key: "car", label: "غسيل سيارات" },
  { key: "home", label: "تنظيف منازل" },
  { key: "laundry", label: "مغاسل ملابس" },
];

const sectionTitle = "mb-4 text-base font-semibold text-ghasla-text-heading";
const card = "rounded-2xl border border-[#F0F6F6] bg-[#FAFDFD] p-5";

export default function ServicePricingStep() {
  const [tab, setTab] = useState<ServiceTab>("car");

  // ===== Car wash state =====
  const [services, setServices] = useState<CarService[]>(carServices.map((s) => ({ ...s })));
  const [options, setOptions] = useState<CarOption[]>(carOptions.map((o) => ({ ...o })));
  const [grid, setGrid] = useState(carPriceGrid.map((r) => ({ ...r, prices: [...r.prices] })));

  // ===== Home cleaning state =====
  const [deepClean, setDeepClean] = useState(true);
  const [hourly, setHourly] = useState(true);
  const [elements, setElements] = useState<HomeElement[]>(homeElements.map((e) => ({ ...e })));
  const [hourPrice, setHourPrice] = useState("45");
  const [minHours, setMinHours] = useState("3");
  const [maxHours, setMaxHours] = useState("8");

  // ===== Laundry state =====
  const [garmentList, setGarmentList] = useState<Garment[]>(
    garments.map((g) => ({ ...g, services: g.services.map((s) => ({ ...s })) })),
  );
  const [expanded, setExpanded] = useState<number>(1);
  const [deliveryModes, setDeliveryModes] = useState<DeliveryMode[]>(delivery.map((d) => ({ ...d })));
  const [freeDelivery, setFreeDelivery] = useState(true);
  const [freeDeliveryAmount, setFreeDeliveryAmount] = useState("100");

  const toggleService = (id: number) => {
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  };

  const toggleOption = (id: number) => {
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o)));
  };

  const toggleElement = (id: number) => {
    setElements((prev) => prev.map((e) => (e.id === id ? { ...e, enabled: !e.enabled } : e)));
  };

  const toggleGarment = (id: number) => {
    setGarmentList((prev) => prev.map((g) => (g.id === id ? { ...g, enabled: !g.enabled } : g)));
  };

  const updateGarmentService = (garmentId: number, serviceName: string, price: string) => {
    setGarmentList((prev) =>
      prev.map((g) =>
        g.id === garmentId
          ? {
              ...g,
              services: g.services.map((s) => (s.name === serviceName ? { ...s, price } : s)),
            }
          : g,
      ),
    );
  };

  const toggleGarmentService = (garmentId: number, serviceName: string) => {
    setGarmentList((prev) =>
      prev.map((g) =>
        g.id === garmentId
          ? {
              ...g,
              services: g.services.map((s) =>
                s.name === serviceName ? { ...s, enabled: !s.enabled } : s,
              ),
            }
          : g,
      ),
    );
  };

  const updateDelivery = (index: number, field: "price" | "duration", value: string) => {
    setDeliveryModes((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  const priceCell =
    "h-9 w-20 rounded-lg border border-[#E4EFEF] bg-white px-2 text-center text-sm outline-none focus:border-[#0D6E6E]";

  return (
    <div>
      {/* Service type tabs */}
      <div className="mb-6 flex gap-2 rounded-full bg-[#F0FDFD] p-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.key ? "bg-[#0D6E6E] text-white" : "text-ghasla-text-secondary hover:text-ghasla-text-primary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ============ CAR WASH ============ */}
      {tab === "car" && (
        <div className="space-y-6">
          {/* Services */}
          <div>
            <h4 className={sectionTitle}>الخدمات</h4>
            <div className={card}>
              {services.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between border-b border-[#F0F6F6] py-3 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Toggle checked={s.enabled} onChange={() => toggleService(s.id)} size="sm" />
                    <div>
                      <div className="text-sm font-semibold text-ghasla-text-heading">{s.name}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-ghasla-text-secondary">
                        <span>{s.desc}</span>
                        <span>•</span>
                        <span>{s.duration}</span>
                      </div>
                    </div>
                  </div>
                  <button type="button" className="cursor-pointer text-xs font-medium text-ghasla-text-link hover:underline">
                    تعديل
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#C9DEDE] py-3 text-sm font-medium text-ghasla-text-link transition-colors hover:border-[#0D6E6E]"
              >
                <i className="ri-add-line text-lg" />
                إضافة خدمة
              </button>
            </div>
          </div>

          {/* Price grid */}
          <div>
            <h4 className={sectionTitle}>الأسعار حسب نوع السيارة</h4>
            <p className="mb-3 text-xs text-ghasla-text-secondary">حدد سعر كل خدمة لكل نوع سيارة</p>
            <div className={`${card} overflow-x-auto p-0`}>
              <table className="w-full min-w-[480px] text-sm">
                <thead>
                  <tr className="border-b border-[#F0F6F6] text-xs font-semibold text-ghasla-text-secondary">
                    <th className="h-11 px-4 text-right">الخدمة</th>
                    {vehicleTypes.map((v) => (
                      <th key={v} className="h-11 px-2 text-center">{v}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.map((row) => (
                    <tr key={row.service} className="border-b border-[#F0F6F6] last:border-0">
                      <td className="h-12 px-4 text-ghasla-text-primary">{row.service}</td>
                      {row.prices.map((price, i) => (
                        <td key={i} className="h-12 px-2 text-center">
                          <input
                            className={priceCell}
                            value={price}
                            onChange={(e) =>
                              setGrid((prev) =>
                                prev.map((r, ri) =>
                                  ri === prev.findIndex((x) => x.service === row.service)
                                    ? {
                                        ...r,
                                        prices: r.prices.map((p, pi) => (pi === i ? e.target.value : p)),
                                      }
                                    : r,
                                ),
                              )
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional options */}
          <div>
            <h4 className={sectionTitle}>خيارات إضافية</h4>
            <div className={card}>
              {options.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between border-b border-[#F0F6F6] py-3 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Toggle checked={o.enabled} onChange={() => toggleOption(o.id)} size="sm" />
                    <span className="text-sm text-ghasla-text-primary">{o.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <input className={priceCell} value={o.price} onChange={() => {}} />
                    <span className="text-xs text-ghasla-text-secondary">ر.س</span>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#C9DEDE] py-3 text-sm font-medium text-ghasla-text-link transition-colors hover:border-[#0D6E6E]"
              >
                <i className="ri-add-line text-lg" />
                إضافة خيار
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ HOME CLEANING ============ */}
      {tab === "home" && (
        <div className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-2xl bg-[#FAFDFD] px-5 py-4">
              <span className="text-sm font-semibold text-ghasla-text-heading">تنظيف شامل</span>
              <Toggle checked={deepClean} onChange={setDeepClean} />
            </div>
            <div className="flex items-center justify-between rounded-2xl bg-[#FAFDFD] px-5 py-4">
              <span className="text-sm font-semibold text-ghasla-text-heading">عاملات بالساعة</span>
              <Toggle checked={hourly} onChange={setHourly} />
            </div>
          </div>

          {deepClean && (
            <div>
              <h4 className={sectionTitle}>تسعير العناصر</h4>
              <div className={card}>
                {elements.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center justify-between border-b border-[#F0F6F6] py-3 last:border-0"
                  >
                    <span className="text-sm text-ghasla-text-primary">{e.name}</span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <input className={priceCell} value={e.price} onChange={() => {}} />
                        <span className="text-xs text-ghasla-text-secondary">ر.س</span>
                      </div>
                      <Toggle checked={e.enabled} onChange={() => toggleElement(e.id)} size="sm" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hourly && (
            <div>
              <h4 className={sectionTitle}>إعدادات الساعة</h4>
              <div className={`${card} grid grid-cols-3 gap-4`}>
                <div>
                  <label className="mb-2 block text-xs font-medium text-ghasla-text-primary">سعر الساعة (ر.س)</label>
                  <input
                    className="h-10 w-full rounded-lg border border-[#E4EFEF] bg-white px-3 text-center text-sm outline-none focus:border-[#0D6E6E]"
                    value={hourPrice}
                    onChange={(e) => setHourPrice(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium text-ghasla-text-primary">الحد الأدنى للساعات</label>
                  <input
                    className="h-10 w-full rounded-lg border border-[#E4EFEF] bg-white px-3 text-center text-sm outline-none focus:border-[#0D6E6E]"
                    value={minHours}
                    onChange={(e) => setMinHours(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium text-ghasla-text-primary">الحد الأقصى للساعات</label>
                  <input
                    className="h-10 w-full rounded-lg border border-[#E4EFEF] bg-white px-3 text-center text-sm outline-none focus:border-[#0D6E6E]"
                    value={maxHours}
                    onChange={(e) => setMaxHours(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============ LAUNDRY ============ */}
      {tab === "laundry" && (
        <div className="space-y-6">
          {/* Available garments */}
          <div>
            <h4 className={sectionTitle}>القطع المتاحة</h4>
            <p className="mb-3 text-xs text-ghasla-text-secondary">فعّل القطع اللي تشتغل عليها من القائمة الرئيسية</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {garmentList.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => toggleGarment(g.id)}
                  className={`flex cursor-pointer items-center justify-between rounded-xl border px-3 py-3 transition-colors ${
                    g.enabled ? "border-[#0D6E6E] bg-[#F0FDFD]" : "border-[#E4EFEF] bg-white"
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm text-ghasla-text-primary">
                    <i className={`${g.icon} text-ghasla-text-secondary`} />
                    {g.name}
                  </span>
                  <i
                    className={`${
                      g.enabled ? "ri-checkbox-circle-fill text-ghasla-text-link" : "ri-checkbox-blank-circle-line text-[#D5E3E3]"
                    } text-lg`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Per-garment services */}
          <div>
            <h4 className={sectionTitle}>أسعار الخدمات لكل قطعة</h4>
            <div className={`${card} space-y-2 p-0`}>
              {garmentList
                .filter((g) => g.enabled)
                .map((g) => (
                  <div key={g.id} className="border-b border-[#F0F6F6] last:border-0">
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === g.id ? -1 : g.id)}
                      className="flex w-full cursor-pointer items-center justify-between px-5 py-3.5 text-sm font-semibold text-ghasla-text-heading"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${g.icon} text-ghasla-text-link`} />
                        {g.name}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line text-lg text-ghasla-text-secondary transition-transform ${
                          expanded === g.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {expanded === g.id && (
                      <div className="px-5 pb-4">
                        <div className="mb-2 grid grid-cols-[1fr_100px_60px] gap-2 px-2 text-xs font-semibold text-ghasla-text-secondary">
                          <span>الخدمة</span>
                          <span className="text-center">السعر (ر.س)</span>
                          <span className="text-center">مفعّل</span>
                        </div>
                        {g.services.map((s) => (
                          <div
                            key={s.name}
                            className="grid grid-cols-[1fr_100px_60px] items-center gap-2 rounded-lg px-2 py-2 hover:bg-[#F0FDFD]"
                          >
                            <span className="text-sm text-ghasla-text-primary">{s.name}</span>
                            <input
                              className="h-8 w-full rounded-lg border border-[#E4EFEF] bg-white px-2 text-center text-sm outline-none focus:border-[#0D6E6E]"
                              value={s.price}
                              onChange={(e) => updateGarmentService(g.id, s.name, e.target.value)}
                            />
                            <div className="flex justify-center">
                              <Toggle
                                checked={s.enabled}
                                onChange={() => toggleGarmentService(g.id, s.name)}
                                size="sm"
                              />
                            </div>
                          </div>
                        ))}

                        {g.hasOptions && (
                          <div className="mt-3 rounded-xl bg-[#F0FDFD] p-4">
                            <div className="mb-2 text-xs font-semibold text-ghasla-text-heading">الخيارات الإضافية</div>
                            <div className="flex flex-wrap gap-2">
                              {(g.options ?? []).map((opt) => (
                                <span
                                  key={opt}
                                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[#C9DEDE] px-3 py-1.5 text-xs text-ghasla-text-primary"
                                >
                                  {opt}
                                  <i className="ri-close-line text-ghasla-text-secondary" />
                                </span>
                              ))}
                              <button
                                type="button"
                                className="inline-flex cursor-pointer items-center gap-1 rounded-full border-2 border-dashed border-[#C9DEDE] px-3 py-1.5 text-xs font-medium text-ghasla-text-link hover:border-[#0D6E6E]"
                              >
                                <i className="ri-add-line" />
                                إضافة خيار
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>

          {/* Wash types */}
          <div>
            <h4 className={sectionTitle}>أسعار أنواع الغسيل</h4>
            <p className="mb-3 text-xs text-ghasla-text-secondary">السعر الأساسي هو للغسيل العادي. حدد الزيادة لباقي الأنواع</p>
            <div className={`${card} p-0`}>
              {washTypes.map((w) => (
                <div
                  key={w.id}
                  className="flex items-center justify-between border-b border-[#F0F6F6] px-5 py-3 last:border-0"
                >
                  <span className="text-sm text-ghasla-text-primary">{w.name}</span>
                  {w.mode === "base" ? (
                    <span className="text-xs text-ghasla-text-secondary">{w.value}</span>
                  ) : w.mode === "percent" ? (
                    <input className={priceCell} value={w.value.replace("%", "")} onChange={() => {}} />
                  ) : (
                    <button type="button" className="cursor-pointer text-xs font-medium text-ghasla-text-link hover:underline">
                      {w.value}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Delivery settings */}
          <div>
            <h4 className={sectionTitle}>إعدادات التوصيل</h4>
            <div className={`${card} p-0`}>
              {deliveryModes.map((d, i) => (
                <div
                  key={d.name}
                  className="flex items-center justify-between border-b border-[#F0F6F6] px-5 py-3 last:border-0"
                >
                  <span className="text-sm font-medium text-ghasla-text-heading">{d.name}</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <input
                        className={priceCell}
                        value={d.price}
                        onChange={(e) => updateDelivery(i, "price", e.target.value)}
                      />
                      <span className="text-xs text-ghasla-text-secondary">ر.س</span>
                    </div>
                    <input
                      className="h-9 w-24 rounded-lg border border-[#E4EFEF] bg-white px-2 text-center text-sm outline-none focus:border-[#0D6E6E]"
                      value={d.duration}
                      onChange={(e) => updateDelivery(i, "duration", e.target.value)}
                    />
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <Toggle checked={freeDelivery} onChange={setFreeDelivery} size="sm" />
                  <span className="text-sm text-ghasla-text-primary">توصيل مجاني للطلبات فوق</span>
                </div>
                <div className="flex items-center gap-1">
                  <input
                    className={priceCell}
                    value={freeDeliveryAmount}
                    onChange={(e) => setFreeDeliveryAmount(e.target.value)}
                  />
                  <span className="text-xs text-ghasla-text-secondary">ر.س</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}